import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { apiFetch } from '../services/apiClient';

// Fix Leaflet's default marker icon (broken by webpack asset handling).
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Read-only map of a project's saved location. Just shows where the job is —
// no draggable pin, nothing to accidentally move. Pulls the saved coordinates
// from the Equipment Locations feed (/projects-map).
// onPick (optional): opens a map to drop / move the pin (the job page passes it).
// qbTime (optional): shows the job's QuickBooks Time job code + location, and the button that sends them.
export default function JobLocationModal({ projectId, address, label, onClose, onPick, refreshKey = 0, qbTime = false }) {
  const [loc, setLoc] = useState(undefined); // undefined=loading, null=none

  useEffect(() => {
    if (!projectId) { setLoc(null); return; }
    // Asks for this one job (the server places it from the bid's address if it has no
    // point yet), rather than loading every job's point and searching the list.
    apiFetch(`/projects/${projectId}/map-point`)
      .then(p => setLoc(p && p.lat != null && p.lng != null ? { lat: p.lat, lng: p.lng, label: p.name || label } : null))
      .catch(() => setLoc(null));
  }, [projectId, label, refreshKey]);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 6000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: 640, maxWidth: '100%', overflow: 'hidden', boxShadow: '0 16px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ background: '#1e3a5f', padding: '12px 16px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 14, fontWeight: 700 }}>📍 {label || 'Job Location'}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        {address && <div style={{ padding: '10px 16px', fontSize: 13, color: '#374151', borderBottom: '1px solid #eef0f2' }}>{address}</div>}
        {loc === undefined ? (
          <div style={{ padding: 50, textAlign: 'center', color: '#9ca3af' }}>Loading map…</div>
        ) : loc === null ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
            No saved location for this job yet.
            {onPick ? (
              <div style={{ marginTop: 12 }}>
                <button onClick={onPick} style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: '#1e3a5f', color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>📍 Pick on Map</button>
              </div>
            ) : <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>Use “Pick on Map” in the Job Description to set it.</div>}
          </div>
        ) : (
          <>
            <MapContainer center={[loc.lat, loc.lng]} zoom={15} style={{ height: 400, width: '100%' }}>
              <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <Marker position={[loc.lat, loc.lng]}><Popup>{loc.label || address || 'Job'}</Popup></Marker>
            </MapContainer>
            <div style={{ padding: '10px 16px', borderTop: '1px solid #eef0f2', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 14 }}>
              {onPick && <button onClick={onPick} style={{ background: 'none', border: 'none', padding: 0, fontSize: 12, fontWeight: 700, color: '#1d4ed8', cursor: 'pointer' }}>📍 Move the pin</button>}
              <a href={`https://www.google.com/maps?q=${loc.lat},${loc.lng}`} target="_blank" rel="noreferrer"
                style={{ fontSize: 12, fontWeight: 700, color: '#1d4ed8' }}>Open in Google Maps ›</a>
            </div>
          </>
        )}
        {qbTime && projectId && <QbTimeJob projectId={projectId} refreshKey={refreshKey} />}
      </div>
    </div>
  );
}

// The job in QuickBooks Time (Pat, Oct 8 2026): its job code — made with the job's name, everyone can
// clock into it — and its location from the pin here. Only on a press; never a second code for a number.
// Also on the job page's QuickBooks Time tile (Ryan, Oct 14 2026: "the same button in the TSheets tile")
// — `inTile` drops the window's divider, `onDone` re-reads the tile's hours after a send.
export function QbTimeJob({ projectId, refreshKey, inTile = false, onDone }) {
  const [st, setSt] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [rename, setRename] = useState(false);
  useEffect(() => {
    setSt(null); setErr('');
    apiFetch(`/projects/${projectId}/qbtime`).then(setSt).catch(e => setErr(e?.error || 'Could not read QuickBooks Time.'));
  }, [projectId, refreshKey]);
  async function send() {
    setBusy(true); setErr(''); setDone(null);
    try { const r = await apiFetch(`/projects/${projectId}/qbtime`, { method: 'POST', body: JSON.stringify({ rename }) }); setSt(r); setDone(r); setRename(false); onDone && onDone(r); }
    catch (e) { setErr(e?.error || 'QuickBooks Time did not take it.'); if (e && e.jobcodes) setSt(e); }
    setBusy(false);
  }
  const code = st && (st.jobcodes || []).find(c => c.active) || (st && st.jobcodes && st.jobcodes[0]);
  const hasLoc = !!(code && code.locations && code.locations.length);
  const nameDiffers = !!(code && st && code.name !== st.wanted_name);
  // A job code made by hand or before Oct 21 2026 may have QB Time's short code blank — the button fills it.
  const needsShort = !!(code && code.active !== false && !String(code.short_code || '').trim() && st && st.wanted_short_code);
  const nothingToDo = !!(code && hasLoc && !needsShort && !(nameDiffers && rename));
  return (
    <div style={inTile ? { padding: '11px 13px', border: '1px solid #e5e7eb', borderRadius: 10, background: '#f8fafc', fontSize: 13, color: '#374151' } : { padding: '12px 16px', borderTop: '1px solid #eef0f2', background: '#f8fafc', fontSize: 13, color: '#374151' }}>
      <div style={{ fontWeight: 800, marginBottom: 6 }}>⏱ QuickBooks Time</div>
      {!st && !err && <div style={{ color: '#9ca3af' }}>Checking QuickBooks Time…</div>}
      {st && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div>Job code: {code ? <b>{code.name}</b> : <span style={{ color: '#b45309', fontWeight: 700 }}>none yet — this makes “{st.wanted_name}”, everyone can clock into it</span>}{code && code.active === false ? <span style={{ color: '#b91c1c', fontWeight: 700 }}> (archived)</span> : null}</div>
          {nameDiffers && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
              <input type="checkbox" checked={rename} onChange={e => setRename(e.target.checked)} /> Rename it to “{st.wanted_name}”
            </label>
          )}
          {st.wanted_short_code && <div>Short code: {code && String(code.short_code || '').trim() ? <b>{code.short_code}</b> : <span style={{ color: '#b45309' }}>{code ? 'blank — this adds' : 'this sets'} <b>{st.wanted_short_code}</b></span>}</div>}
          <div>Location: {hasLoc ? <b>{code.locations.map(l => l.label).join(' · ')}</b> : st.place ? <span>none yet — this adds <b>{st.place.address || 'the pin above'}</b></span> : <span style={{ color: '#b45309' }}>none — set the job's pin first</span>}</div>
        </div>
      )}
      {err && <div style={{ color: '#b91c1c', fontWeight: 700, marginTop: 6 }}>{err}</div>}
      {done && <div style={{ color: '#15803d', fontWeight: 700, marginTop: 6 }}>{done.did && done.did.length ? '✓ ' + done.did.join('; ') : '✓ Nothing needed changing'}{done.note ? <span style={{ color: '#b45309' }}> · {done.note}</span> : null}</div>}
      {st && (
        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={send} disabled={busy || nothingToDo} title={nothingToDo ? 'QuickBooks Time already has this job and its location' : 'Make or update the job code and its location in QuickBooks Time'}
            style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: busy || nothingToDo ? '#e5e7eb' : '#15803d', color: busy || nothingToDo ? '#9ca3af' : '#fff', fontSize: 13, fontWeight: 800, cursor: busy || nothingToDo ? 'default' : 'pointer' }}>
            {/* The first time it reads "Add to TSheets" (Ryan, Oct 14 2026) — the name the crews use. */}
            {busy ? 'Sending…' : nothingToDo ? '✓ In QuickBooks Time' : !code ? '⏱ Add to TSheets' : '⏱ Send to QuickBooks Time'}
          </button>
        </div>
      )}
    </div>
  );
}
